import React, { useCallback, useEffect, useMemo } from "react";
import { useMachine } from "@xstate/react";
import EpisodeChart from "./EpisodeChart";
import { Route, Routes, useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import ReportTitle from "./ReportTitle";
import PeriodNav from "./PeriodNav";
import { css } from "@emotion/react";
import EpisodeDataGrid from "./EpisodeDataGrid";
import ClientDataGrid from "./ClientDataGrid";
import CountryDataGrid from "./CountryDataGrid";

import axios from "axios";
import debounce from "lodash-es/debounce";

import reportMachine from "./report-machine";
import formats from "./date-formats";
import { useAccount, useAccountID } from "../../../store/account";
import { useShowID } from "../../../store/show";
import HistoricalDataMessage from "../../../shared/HistoricalDataMessage";
import PaidFeatureButton from "../../../shared/PaidFeatureButton";
import ExportButton from "../../../shared/ExportButton";

const numberFormat = Intl.NumberFormat("en-US").format;
const percentFormat = Intl.NumberFormat("en-US", {
    style: "percent",
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
}).format;

// This is a custom hook to avoid namespace collisions between the
// url params and the actual report params from the state machine.
function useSynchedParams(params, send) {
    const { podcastID, report, period, reportStart, reportEnd } = params;

    useEffect(
        () => {
            //console.log("sending PARAMS from useEffect", params);
            send("PARAMS", params);
        },
        [podcastID, report, period, reportStart, reportEnd]
    );
}

const makeUrl = (accountID, podcastID, report, period, reportStart, reportEnd) =>
    `/${[
        "dashboard",
        accountID,
        "shows",
        podcastID,
        "downloads",
        report,
        period,
        reportStart,
        reportEnd
    ]
        .filter(p => p)
        .join("/")}`;

export default function MeasurementReport() {
    const navigate = useNavigate();
    const accountID = useAccountID();
    const podcastID = useShowID();
    const { data: account } = useAccount();
    const params = { ...useParams(), accountID, podcastID };

    const [state, send] = useMachine(reportMachine, {
        context: { ...params },
        actions: {
            // when the params inside the state machine change, we synchronize them with the url
            syncParams: (_, { data: { podcastID, report, period, reportStart, reportEnd } }) =>
                navigate(makeUrl(accountID, podcastID, report, period, reportStart, reportEnd))
        }
    });

    //...and when the url params change we push them to the state machine
    useSynchedParams(params, send);

    const { value, context } = state;
    const {
        podcast,
        report,
        period,
        reportStart,
        reportEnd,
        bounds,
        canned,
        reportTotal,
        matrix,
        columns,
        rowCount = 50, // defaults so we draw some empty grid cells when
        columnCount = 20, // there's no data, instead of a blank space
        columnTotals,
        columnTotalsFormatted,
        columnsFormatted,
        rowTotals,
        cells
    } = context;

    // Memoize the data used by the charts
    const [chartExtent, chartData] = useMemo(
        () => {
            if (columnTotals) {
                return [
                    matrix.columnExtent,
                    matrix.columns.map(period => [period, columnTotals[period]])
                ];
            } else {
                return [[reportStart, reportEnd], []];
            }
        },
        [matrix, columnTotals, reportStart, reportEnd]
    );

    // These are the callback functions used to render the data grid.
    // TODO: we should probably stop stuffing these into one object since
    // not all of the dependencies will change every function, so we're probably
    // triggering non-optimal re-renders.
    const gridData = useMemo(
        () => ({
            getShowTitle: () => podcast && podcast.title,
            getGrandTotal: () => (reportTotal >= 0 ? numberFormat(reportTotal) : ""),
            totalHeader: columnIndex =>
                columns && columnTotalsFormatted ? columnTotalsFormatted[columns[columnIndex]] : "",
            periodHeader: columnIndex => (columnsFormatted ? columnsFormatted[columnIndex] : ""),
            getRowTitle: rowIndex => {
                if (rowTotals) {
                    const row = rowTotals[rowIndex];
                    return row ? row.title : "…";
                }
                return "";
            },
            cellValue: (columnIndex, rowIndex) =>
                cells && cells[rowIndex] ? cells[rowIndex][columnIndex] : "…",
            getRowDate: rowIndex => {
                if (rowTotals) {
                    const row = rowTotals[rowIndex];
                    return row && row.publicationDate
                        ? moment(row.publicationDate).format(formats.day.short)
                        : "…";
                }
                return "";
            },
            getRowTotal: rowIndex => {
                if (rowTotals) {
                    const row = rowTotals[rowIndex];
                    if (row && row.uniqueDownloads >= 0) return numberFormat(row.uniqueDownloads);
                }
                return "";
            },
            getRowPercent: rowIndex => {
                if (rowTotals) {
                    const row = rowTotals[rowIndex];
                    if (row && row.uniqueDownloads >= 0 && reportTotal > 0)
                        return percentFormat(row.uniqueDownloads / reportTotal);
                }
                return "";
            }
        }),
        [podcast, columns, columnsFormatted, columnTotalsFormatted, reportTotal, rowTotals, cells]
    );

    // This is the callback used by the grid to tell us what position it's at.
    // We send that as event into the state machine and it figures out what data
    // it needs to load if any.
    const setVisibleRange = useCallback(
        // this is debounced so it can be called during scroll without going haywire
        debounce(
            (colStart, rowStart, colEnd, rowEnd) => {
                send("VIEWPORT", { viewPort: { colStart, rowStart, colEnd, rowEnd } });
            },
            200, // repeated calls before this many ms have elapsed are swallowed
            {
                leading: true, // but fire the first call immediately
                maxWait: 1000, // and let one through every maxWait ms even if the timer hasn't expired
                trailing: true // and make sure the last call is fired after the timer expires
            }
        ),
        [send]
    );

    return (
        <>
            <ReportTitle
                makeUrl={makeUrl}
                period={period}
                report={report}
                podcast={podcast}
                reportStart={reportStart}
                reportEnd={reportEnd}
            />
            <HistoricalDataMessage />
            <div
                css={css`
                    display: flex;
                    margin: 1em 0 0.25em;
                    align-items: center;
                `}
            >
                <div style={{ flex: 1 }} />
                <PeriodNav makeUrl={makeUrl} bounds={bounds} canned={canned} />
                <select
                    style={{ float: "none", width: "10em" }}
                    value={`${reportStart}/${reportEnd}`}
                    onChange={event =>
                        event.target.value &&
                        navigate(makeUrl(accountID, podcastID, report, period, event.target.value))
                    }
                >
                    <option value="" />
                    {bounds &&
                        canned &&
                        canned[period] &&
                        canned[period].map((range, i) => (
                            <option value={range.key} key={i}>
                                {range.name}
                            </option>
                        ))}
                </select>
                {account ? (
                    <div style={{ textAlign: "right", marginLeft: "1rem" }}>
                        <PaidFeatureButton
                            hasFeature={account.isPaidPlan}
                            dialogContent="Export of the Download report data currently displayed on your screen to an Excel file is a premium feature available in the Grow Your Show Plan."
                        >
                            {({ hasFeature, openModal }) => {
                                return (
                                    <ExportButton
                                        onClick={
                                            //Advertise exports if they aren't enabled
                                            !hasFeature ? openModal
                                                //Queue exports we need to be generated in the background
                                                : report == 'episode' ? () => {
                                                    axios.get(`/__/${account.accountID
                                                        }/reports/${report}-measurement-export/${podcast.podcastID
                                                        }/${period}/${reportStart}/${reportEnd}`)
                                                        .then(res => {
                                                            //var id = res.data //id to check status with eventually with calls to episode-measurement-export-progress/id
                                                            alert('Your report will be emailed to you shortly.');
                                                        })
                                                    
                                                }
                                                //Redirect straight to quicker reports
                                                : () =>
                                                      (window.location = `/__/${
                                                          account.accountID
                                                      }/reports/${report}-measurement-export/${
                                                          podcast.podcastID
                                                      }/${period}/${reportStart}/${reportEnd}`)
                                        }
                                    />
                                );
                            }}
                        </PaidFeatureButton>
                    </div>
                ) : null}
            </div>
            {/* TODO: chart should be in route when we have report-specific charts */}
            {reportStart ? <EpisodeChart dateExtent={chartExtent} data={chartData} /> : null}
            {report == "client" ? (
                <>
                    <ClientDataGrid
                        columnCount={columnCount}
                        rowCount={rowCount}
                        gridData={gridData}
                        onGridRender={setVisibleRange}
                    />
                    <p className="note" style={{ marginTop: 10 }}>
                        * Client data is currently available from 1/1/2020 forward.
                    </p>
                </>
            ) : report == "country" ? (
                <CountryDataGrid
                    columnCount={columnCount}
                    rowCount={rowCount}
                    gridData={gridData}
                    onGridRender={setVisibleRange}
                />
            ) : (
                <EpisodeDataGrid
                    columnCount={columnCount}
                    rowCount={rowCount}
                    gridData={gridData}
                    onGridRender={setVisibleRange}
                />
            )}
            {/*<pre className="notice">
                {JSON.stringify({ value, viewPort: context.viewPort, cells }, null, 2)}
            </pre>*/}
        </>
    );
}
